/**
 * ThreadActivityModal — two-step activity logger for Threads.
 *
 * Step 1: Choose an activity type from a filtered card grid. The available
 *   types depend on the thread's current status — a closed thread can only
 *   be Reopened, an open thread cannot show "Thread Reopened", and "Thread
 *   Opened" is never user-selectable (auto-logged at creation).
 *
 * Step 2: Enter type-specific details. Most types need a date + notes, but
 *   some have extra context (Deadline Changed adds a new-deadline picker;
 *   Thread Closed / Thread Reopened show a lifecycle consequence note).
 *
 * Opens via: CustomEvent('open-thread-activity-modal', {
 *   detail: { threadId, threadTitle, threadStatus?, presetType? }
 * })
 *
 * `presetType` (e.g. 'Thread Closed') skips step 1 and jumps straight to step 2 pre-selected —
 * used by dragging a thread to the far right of the Flight Tracker's curve, so that single
 * gesture lands on "write the closing note" immediately rather than the full type picker. Only
 * applied when the thread isn't already closed (closed threads always go to Reopen, unchanged).
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { logThreadActivity, updateThread, getThreadById } from '../services/threads.js'
import { useScrollLock } from '../hooks/useScrollLock.js'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

/* ── Activity type definitions ──────────────────────────────────────────── */

const ACTIVITY_TYPES = [
  {
    type: 'Action Taken',
    icon: '✓',
    label: 'Action Taken',
    description: 'Something was done to advance the thread.',
    when: 'open',         // 'open' | 'closed' | 'always'
    notesLabel: 'What was done',
    notesRequired: true,
  },
  {
    type: 'Meeting Discussion',
    icon: '💬',
    label: 'Meeting Discussion',
    description: 'This thread was discussed in a meeting.',
    when: 'open',
    notesLabel: 'Meeting notes',
    notesRequired: false,
  },
  {
    type: 'Reassigned',
    icon: '↔',
    label: 'Reassigned',
    description: 'Responsibility was transferred to someone else.',
    when: 'open',
    notesLabel: 'Details',
    notesRequired: false,
  },
  {
    type: 'Item Delegated',
    icon: '↓',
    label: 'Item Delegated',
    description: 'A specific item was delegated.',
    when: 'open',
    notesLabel: 'Details',
    notesRequired: false,
  },
  {
    type: 'Item Completed',
    icon: '★',
    label: 'Item Completed',
    description: 'The item is done. This closes the thread.',
    when: 'open',
    notesLabel: 'What was completed',
    notesRequired: false,
    lifecycle: 'close',
  },
  {
    type: 'Thread Closed',
    icon: '🔒',
    label: 'Close Thread',
    description: 'Mark this thread as resolved and close it.',
    when: 'open',
    notesLabel: 'Closing note',
    notesRequired: false,
    lifecycle: 'close',
  },
  {
    type: 'Thread Reopened',
    icon: '🔓',
    label: 'Reopen Thread',
    description: 'This thread needs attention again — reopen it.',
    when: 'closed',
    notesLabel: 'Reason for reopening',
    notesRequired: false,
    lifecycle: 'reopen',
  },
]

function availableTypes(isClosed) {
  return ACTIVITY_TYPES.filter(t =>
    t.when === 'always' ||
    (t.when === 'open'   && !isClosed) ||
    (t.when === 'closed' && isClosed)
  )
}

/* ── Shared styles ──────────────────────────────────────────────────────── */

const S = {
  label: {
    display: 'block',
    fontFamily: '"DM Mono",monospace',
    fontSize: 10,
    color: 'var(--aj-text-mid)',
    letterSpacing: '0.04em',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '7px 10px',
    fontFamily: '"DM Sans",sans-serif',
    fontSize: 12,
    color: 'var(--aj-text)',
    background: 'var(--aj-bg-subtle)',
    border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4,
    boxSizing: 'border-box',
    outline: 'none',
  },
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function ThreadActivityModal() {
  const [open,        setOpen]       = useState(false)
  const [threadId,    setThreadId]   = useState(null)
  const [threadTitle, setTitle]      = useState('')
  const [isClosed,    setIsClosed]   = useState(false)
  const [step,        setStep]       = useState(1)    // 1 = pick type, 2 = enter details
  const [selectedDef, setSelectedDef] = useState(null) // the ACTIVITY_TYPES entry
  const [actDate,     setActDate]    = useState('')
  const [notes,       setNotes]      = useState('')
  const [submitting,  setSubmitting] = useState(false)
  const [error,       setError]      = useState(null)

  // Reference-counted (hooks/useScrollLock.js) — this can open ON TOP of a workspace (Pulse's
  // Flight Tracker, dragging a thread to the far right) that already holds its own lock.
  useScrollLock(open)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      const tid = d.threadId || null
      setThreadId(tid)
      setTitle(d.threadTitle || 'Thread')

      // Determine current closed state from the cache or the passed status
      let closed = false
      if (d.threadStatus) {
        closed = String(d.threadStatus).includes('Closed')
      } else if (tid && Array.isArray(ctx.threadCache)) {
        const cached = ctx.threadCache.find(t => t.id === tid)
        if (cached) closed = cached.fields['Closed'] === true || (cached.fields['Status'] || '').includes('Closed')
      } else if (tid && ctx.caThreadCache?.[tid]?.fields) {
        const f = ctx.caThreadCache[tid].fields
        closed = f['Closed'] === true || (f['Status'] || '').includes('Closed')
      }
      setIsClosed(closed)

      // For a closed thread there is only one valid action: Reopen.
      // Skip the type picker and go straight to Step 2.
      const reopenDef = ACTIVITY_TYPES.find(t => t.type === 'Thread Reopened')
      // presetType (e.g. dragged to the far right of the Flight Tracker meaning "close this")
      // skips the picker the same way, landing straight on step 2 already selected — only when
      // the thread isn't already closed, since a closed one only ever offers Reopen anyway.
      const presetDef = !closed && d.presetType ? ACTIVITY_TYPES.find(t => t.type === d.presetType) : null
      if (closed) {
        setSelectedDef(reopenDef)
        setStep(2)
      } else if (presetDef) {
        setSelectedDef(presetDef)
        setStep(2)
      } else {
        setSelectedDef(null)
        setStep(1)
      }
      setActDate(todayIso())
      setNotes('')
      setError(null)
      setSubmitting(false)
      setOpen(true)
    }
    window.addEventListener('open-thread-activity-modal', onOpen)
    return () => window.removeEventListener('open-thread-activity-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) {
      if (!open) return
      if (e.key === 'Escape') {
        if (step === 2 && !isClosed) { setStep(1); setError(null) }
        else setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, step])

  function selectType(def) {
    setSelectedDef(def)
    setNotes('')
    setError(null)
    setStep(2)
  }

  async function handleSubmit() {
    if (!selectedDef || !actDate || !threadId) return
    if (selectedDef.notesRequired && !notes.trim()) return
    const memberId = wf.currentMemberId
    if (!memberId) return

    setSubmitting(true)
    setError(null)
    try {
      const res = await logThreadActivity(
        threadId, selectedDef.type, actDate, notes.trim() || undefined, memberId
      )
      if (res && res.error) throw new Error(res.error)

      // Optimistic patch for instant feedback — Status is a real Airtable formula field
      // (🟢 Open / ⚪ Closed / 🛑 STUCK once normalized by threadStatus), so this is a best-effort guess for the
      // lifecycle cases only; the refetch below corrects it (and everything else) once Airtable
      // has recomputed the formula server-side.
      function patchCache(f) {
        if (!f) return
        if (selectedDef.lifecycle === 'close')   { f['Closed'] = true;  f['Status'] = '⚪ Closed' }
        if (selectedDef.lifecycle === 'reopen')  { f['Closed'] = false; f['Status'] = '🟢 Open' }
        f['Last Activity'] = actDate
      }
      const cached = Array.isArray(ctx.threadCache) ? ctx.threadCache.find(t => t.id === threadId) : null
      if (cached) patchCache(cached.fields)
      if (ctx.caThreadCache?.[threadId]?.fields) patchCache(ctx.caThreadCache[threadId].fields)

      // Bust activity cache so the log refreshes
      if (ctx.threadActCache) ctx.threadActCache[threadId] = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))

      // Authoritative refetch — replaces the optimistic guess with the server-recomputed Status
      // (and any other formula fields) so the row's badge/date are correct without a page reload.
      try {
        const fresh = await getThreadById(threadId)
        if (fresh) {
          if (cached) cached.fields = fresh.fields
          if (ctx.caThreadCache?.[threadId]) ctx.caThreadCache[threadId] = fresh
          window.dispatchEvent(new Event('outline-render'))
        }
      } catch (_) { /* non-fatal — optimistic patch stands until next reload */ }
    } catch (ex) {
      setError(ex.message || 'Failed to log activity')
      setSubmitting(false)
    }
  }

  if (!open) return null

  const types = availableTypes(isClosed)
  const canSubmit = selectedDef && actDate && !submitting &&
    (!selectedDef.notesRequired || notes.trim())

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 600,
      background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div data-modal-id="ThreadActivityModal" style={{
        background: 'var(--aj-bg-elevated)',
        border: '0.5px solid var(--aj-border)',
        borderRadius: 8,
        boxShadow: '0 12px 36px rgba(0,0,0,0.16)',
        width: step === 1 ? 480 : 420,
        padding: '20px 20px 16px',
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
        transition: 'width 0.15s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {step === 2 && !isClosed && (
                <button
                  onClick={() => { setStep(1); setError(null) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 16, lineHeight: 1, padding: '0 4px 0 0' }}
                  title="Back"
                >‹</button>
              )}
              <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
                {step === 1 ? 'Log Activity' : selectedDef?.label}
              </span>
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>
              {threadTitle}
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}
          >×</button>
        </div>

        {/* ── STEP 1: type picker ── */}
        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {types.map(def => (
              <button
                key={def.type}
                onClick={() => selectType(def)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  gap: 4, padding: '10px 12px',
                  background: 'var(--aj-bg-subtle)',
                  border: '0.5px solid var(--aj-border)',
                  borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                  transition: 'background 0.1s, border-color 0.1s',
                  ...(def.lifecycle === 'close'  ? { borderColor: 'rgba(192,57,43,0.3)' } : {}),
                  ...(def.lifecycle === 'reopen' ? { borderColor: 'rgba(29,158,117,0.3)' } : {}),
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-muted)'; e.currentTarget.style.borderColor = 'var(--aj-border-mid)' }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'var(--aj-bg-subtle)'
                  e.currentTarget.style.borderColor = def.lifecycle === 'close' ? 'rgba(192,57,43,0.3)' : def.lifecycle === 'reopen' ? 'rgba(29,158,117,0.3)' : 'var(--aj-border)'
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>{def.icon}</span>
                <span style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
                  color: def.lifecycle === 'close' ? '#c0392b' : def.lifecycle === 'reopen' ? '#1D9E75' : 'var(--aj-text)',
                }}>{def.label}</span>
                <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.35 }}>
                  {def.description}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── STEP 2: details form ── */}
        {step === 2 && selectedDef && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Lifecycle consequence banner */}
            {selectedDef.lifecycle && (
              <div style={{
                padding: '8px 10px', borderRadius: 4, fontSize: 11,
                fontFamily: '"DM Sans",sans-serif',
                background: selectedDef.lifecycle === 'close' ? 'rgba(192,57,43,0.07)' : 'rgba(29,158,117,0.07)',
                color:      selectedDef.lifecycle === 'close' ? '#c0392b' : '#1D9E75',
                border: '0.5px solid ' + (selectedDef.lifecycle === 'close' ? 'rgba(192,57,43,0.2)' : 'rgba(29,158,117,0.2)'),
              }}>
                {selectedDef.lifecycle === 'close'
                  ? 'Saving will close this thread and update its status.'
                  : 'Saving will reopen this thread and update its status.'}
              </div>
            )}

            {/* Date */}
            <div>
              <label style={S.label}>Date <span style={{ color: '#c0392b' }}>*</span></label>
              <input
                type="date" style={S.input}
                value={actDate}
                onChange={e => setActDate(e.target.value)}
                autoFocus
              />
            </div>



            {/* Notes */}
            <div>
              <label style={S.label}>
                {selectedDef.notesLabel || 'Notes'}
                {selectedDef.notesRequired && <span style={{ color: '#c0392b' }}> *</span>}
              </label>
              <textarea
                style={{ ...S.input, resize: 'vertical', minHeight: 72 }}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={selectedDef.notesRequired ? 'Required…' : 'Optional…'}
                autoFocus={!selectedDef.extra}
              />
            </div>

            {error && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              {!isClosed && (
                <button
                  onClick={() => { setStep(1); setError(null) }}
                  style={{
                    padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
                    background: 'none', border: '0.5px solid var(--aj-border-mid)',
                    borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)',
                  }}
                >Back</button>
              )}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
                  background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)',
                  color: canSubmit ? '#fff' : 'var(--aj-text-mid)',
                  border: 'none', borderRadius: 4,
                  cursor: canSubmit ? 'pointer' : 'default',
                  transition: 'background 0.12s',
                }}
              >{submitting ? 'Saving…' : 'Log Activity'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
